import { useMemo, useState } from 'react';

import { Link, Outlet, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import { LinkOutlined, MenuOutlined, TagOutlined, WalletOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Drawer, Layout, Menu } from 'antd';

import logo from '../logo.png';
import { collectionMap } from '../const/CollectionsInfo';
import { colorAccent, colorSider } from '../const/colors';

const { Content, Footer, Sider } = Layout;

const menuItems: MenuProps['items'] = [
    {
        label: 'On sale',
        key: 'on_sale',
        icon: <TagOutlined />,
        children: Object.values(collectionMap).map((collection) => ({
            key: collection.collectionPath,
            label: <Link to={`/${collection.collectionPath}`}>{collection.collectionName}</Link>,
        })),
    },
    {
        label: <Link to='/wallet'>Wallet</Link>,
        key: 'wallet',
        icon: <WalletOutlined />,
    },
    {
        key: 'official_website',
        icon: <LinkOutlined />,
        label: (
            <a href='https://myduckly.com' target='_blank' rel='noopener noreferrer' style={{ color: colorAccent }}>
                Official website
            </a>
        ),
    },
];

const AppShell = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();

    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 991px)' });

    const selectedKey = useMemo(() => {
        const pathKey = location.pathname.split('/')[1];
        return pathKey || 'land';
    }, [location.pathname]);

    const menu = (
        <Menu
            theme='dark'
            selectedKeys={[selectedKey]}
            defaultOpenKeys={['on_sale']}
            mode='inline'
            items={menuItems}
            onClick={() => setDrawerOpen(false)}
            style={{ background: 'transparent', borderInlineEnd: 'none' }}
        />
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {isTabletOrMobile ? (
                <>
                    <header className='mobile-header'>
                        <Link to='/land'>
                            <img src={logo} alt='duckly.info' />
                        </Link>
                        <Button
                            type='text'
                            aria-label='Open menu'
                            icon={<MenuOutlined style={{ fontSize: 20 }} />}
                            onClick={() => setDrawerOpen(true)}
                        />
                    </header>
                    <Drawer
                        placement='right'
                        width={280}
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        styles={{ body: { padding: 0, background: colorSider }, header: { background: colorSider } }}
                        title={<img src={logo} alt='duckly.info' style={{ height: 36 }} />}
                    >
                        {menu}
                    </Drawer>
                </>
            ) : (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={(value) => setCollapsed(value)}
                    width={220}
                    style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'auto' }}
                >
                    <Link to='/land'>
                        <img src={logo} alt='duckly.info' className='sider-logo' />
                    </Link>
                    {menu}
                </Sider>
            )}
            <Layout>
                <Content style={{ paddingTop: isTabletOrMobile ? 56 : 0 }}>
                    <Outlet />
                </Content>
                <Footer style={{ textAlign: 'center', fontSize: 13, opacity: 0.75 }}>
                    {new Date().getFullYear()} · duckly.info is a free community tool, not affiliated with the official
                    game producer. Nothing here is financial advice.
                </Footer>
            </Layout>
        </Layout>
    );
};

export default AppShell;
